import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";
import { cn } from "@/lib/utils";
import { AnimatePresence, motion } from "framer-motion";
import {
  AlertTriangle,
  Ban,
  CheckCircle,
  ChevronDown,
  Clock,
  Package,
  Truck,
  Undo2,
} from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import HistoryCircle from "@/assets/icons/HistoryCircle";

const STATUS_ICON_MAP: Record<string, React.ComponentType<{ className?: string }>> = {
  pending_order: Clock,       // Pedido pendente
  processing_order: Package,  // Em processamento
  shipped: Truck,             // Enviado
  in_transit: Truck,          // Em trânsito
  out_for_delivery: Truck,    // Saiu para entrega
  delivered: CheckCircle,     // Entregue
  delivery_failed: AlertTriangle, // Falha na entrega
  cancelled_order: Ban,       // Cancelado
  returned: Undo2,            // Devolvido
};

export interface TimelineEvent {
  id: string;              // UUID único do evento
  status_key: string;      // Chave do status (pending_order, shipped, etc)
  description: string;     // Descrição legível do evento
  date: string;            // Data formatada "dd/mm/yyyy, HH:mm"
  location?: string | null; // Localização (quando disponível)
  source: string;          // Origem do evento: 'platform' | 'tb3' | 'eagle_labs'
}

interface DeliveryTimelineProps {
  timeline: TimelineEvent[];
  className?: string;
  collapsible?: boolean;
}

const contentVariants = {
  collapsed: {
    height: 0,
    opacity: 0,
    transition: {
      height: { duration: 0.3, ease: "easeInOut" },
      opacity: { duration: 0.2 },
    },
  },
  expanded: {
    height: "auto",
    opacity: 1,
    transition: {
      height: { duration: 0.3, ease: "easeInOut" },
      opacity: { duration: 0.2, delay: 0.1 },
    },
  },
};

const getStatusStyles = (statusKey: string, index: number) => {
  // Primeiro item (mais recente) recebe estilo destacado
  if (index === 0) {
    const isSuccess = statusKey === "delivered";
    const isError = statusKey === "delivery_failed" || statusKey === "cancelled_order";

    if (isSuccess) {
      return { bg: "bg-lime-500/20", icon: "text-lime-500" };
    }
    if (isError) {
      return { bg: "bg-red-500/20", icon: "text-red-500" };
    }
    return { bg: "bg-blue-500/20", icon: "text-blue-500" };
  }

  // Demais itens ficam com estilo neutro
  return { bg: "bg-zinc-700", icon: "text-zinc-400" };
};

const getSourceLabel = (source: string) => {
  switch (source) {
    case "platform": return "Plataforma";
    case "tb3": return "TB3";
    case "eagle_labs": return "Eagle Labs";
    default: return source;
  }
};

export default function DeliveryTimeline({
  timeline,
  className,
  collapsible = false,
}: DeliveryTimelineProps) {
  const { t } = useTranslation(["orders"]);
  const [isExpanded, setIsExpanded] = useState(!collapsible);

  // Não renderiza se não houver eventos
  if (!timeline || timeline.length === 0) {
    return null;
  }

  return (
    <Card className={cn("p-5 md:p-6", className)}>
      {/* Cabeçalho - clicável se collapsible */}
      <div
        className={cn(
          "flex items-center justify-between",
          collapsible ? "cursor-pointer select-none" : "mb-5",
          collapsible && !isExpanded && "mb-0",
          collapsible && isExpanded && "mb-5"
        )}
        onClick={collapsible ? () => setIsExpanded(!isExpanded) : undefined}
      >
        <div className="flex items-center gap-2">
          <HistoryCircle />
          <h2 className="text-colorText font-semibold text-base">
            {t("orders:delivery_timeline.title")}
          </h2>
        </div>
        <div className="flex items-center gap-2">
          {/* Contador de eventos */}
          {collapsible && (
            <div className="flex items-center gap-2 text-xs text-muted-foreground mr-1">
              <span className="flex items-center gap-1">
                <span className="w-1.5 h-1.5 rounded-full bg-lime-500" />
                {timeline.length} {timeline.length === 1 ? "evento" : "eventos"}
              </span>
            </div>
          )}
          {/* Ícone de expandir/recolher */}
          {collapsible && (
            <ChevronDown
              size={16}
              className={cn(
                "text-muted-foreground transition-transform duration-300",
                isExpanded && "rotate-180"
              )}
            />
          )}
        </div>
      </div>

      <AnimatePresence initial={false}>
        {isExpanded && (
          <motion.div
            initial={collapsible ? "collapsed" : false}
            animate="expanded"
            exit="collapsed"
            variants={contentVariants}
            className="overflow-hidden"
          >
            <div className="relative">
              {/* Linha vertical tracejada conectando os eventos */}
              <svg
                className="absolute top-4 bottom-4 left-4 h-[calc(100%-2rem)] w-px"
                style={{ zIndex: 0 }}
              >
                <line
                  x1="0"
                  y1="0"
                  x2="0"
                  y2="100%"
                  stroke="#3f3f46"
                  strokeWidth="4"
                  strokeDasharray="9,8"
                />
              </svg>

              <div className="space-y-4">
                {timeline.map((item, index) => {
                  // Busca ícone baseado no status_key
                  const Icon = STATUS_ICON_MAP[item.status_key] || Clock;
                  const styles = getStatusStyles(item.status_key, index);

                  return (
                    <motion.div
                      key={item.id}
                      initial={{ opacity: 0, x: -10 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: index * 0.03 }}
                      className="relative flex items-start space-x-4"
                    >
                      {/* Ícone do evento */}
                      <div className="relative z-10 flex-shrink-0">
                        <div
                          className={cn(
                            "h-8 w-8 flex items-center justify-center rounded-full border border-zinc-800",
                            styles.bg
                          )}
                        >
                          <Icon className={cn("h-4 w-4", styles.icon)} />
                        </div>
                      </div>

                      {/* Conteúdo do evento */}
                      <div className="min-w-0 flex-1 pb-2">
                        <div className="mb-1 flex items-center justify-between max-sm:flex-col-reverse max-sm:items-start gap-1">
                          <div className="flex items-center gap-2">
                            {/* Título do status traduzido */}
                            <h4 className="font-medium text-colorText">
                              {t(`orders:status.${item.status_key}`)}
                            </h4>
                            {/* Badge com origem do evento (somente se não for plataforma) */}
                            {item.source !== "platform" && (
                              <Badge variant="outline" className="text-[10px] px-1.5 py-0 text-muted-foreground">
                                {getSourceLabel(item.source)}
                              </Badge>
                            )}
                          </div>
                          {/* Data do evento */}
                          <span className="text-muted text-xs">{item.date}</span>
                        </div>

                        {/* Descrição do evento */}
                        <p className="text-muted-foreground text-sm">
                          {item.description}
                        </p>

                        {/* Localização (se disponível) */}
                        {item.location && (
                          <p className="text-muted text-xs mt-1">
                            📍 {item.location}
                          </p>
                        )}
                      </div>
                    </motion.div>
                  );
                })}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </Card>
  );
}
